import React, { useCallback, useEffect, useState } from "react";
import { Joyride, STATUS, ACTIONS, EVENTS } from "react-joyride";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { STYLES, DEFAULT_OPTIONS } from "../OnboardingTour";

// v223: omvisning i nye Hjem. Vises én gang per bruker, kan startes på nytt via «beta-tour-start».
const seenKey = (uid) => `ne_beta_tour_v4_${uid}`;

const pick = (...sels) => sels.find((s) => {
  const el = document.querySelector(s);
  return el && el.offsetParent !== null;
});

const otherDialogOpen = () => [...document.querySelectorAll(".fixed.inset-0, [role='dialog']")].some((el) => {
  const cs = getComputedStyle(el);
  return cs.display !== "none" && cs.visibility !== "hidden" && parseFloat(cs.opacity) > 0.05
    && cs.pointerEvents !== "none" && el.getBoundingClientRect().height > window.innerHeight * 0.5;
});

function buildSteps(t) {
  const body = (key) => <div style={{ whiteSpace: "pre-line" }}>{t(`beta_tour.${key}_c`)}</div>;
  const center = (key) => ({ target: "body", placement: "center", title: t(`beta_tour.${key}_t`), content: body(key) });
  const at = (sel, key) => {
    const target = pick(...[].concat(sel));
    return target ? { target, title: t(`beta_tour.${key}_t`), content: body(key) } : center(key);
  };
  return [
    center("welcome"),
    at(["[data-testid='beta-sidebar']", "[data-testid='beta-mobile-menu']"], "menu"),
    at("[data-testid='beta-feed-list']", "feed"),
    at("[data-testid='beta-follow-toggle']", "tabs"),
    at("[data-testid='beta-home-composer']", "share"),
    at("[data-testid='beta-sidebar-profil']", "profile"),
    center("vis"),
    center("tags"),
    at("[data-testid='beta-sidebar-galleri']", "albums"),
    center("done"),
  ];
}

export default function BetaTour() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState([]);

  const start = useCallback(() => {
    setSteps(buildSteps(t));
    setStepIndex(0);
    setRun(true);
  }, [t]);

  useEffect(() => {
    if (!user?.id) return undefined;
    const onStart = () => start();
    window.addEventListener("beta-tour-start", onStart);
    let timer;
    // Vent til andre dialoger (profil-oppsett, cookies o.l.) er lukket før omvisningen starter.
    const tryStart = () => {
      if (otherDialogOpen()) { timer = setTimeout(tryStart, 2000); return; }
      start();
    };
    if (sessionStorage.getItem("beta-tour-pending")) {
      sessionStorage.removeItem("beta-tour-pending");
      timer = setTimeout(tryStart, 1500);
    } else if (user.onboarding_seen !== false && !localStorage.getItem(seenKey(user.id))) timer = setTimeout(tryStart, 1500);
    return () => { window.removeEventListener("beta-tour-start", onStart); clearTimeout(timer); };
  }, [user?.id, user?.onboarding_seen, start]);

  const onEvent = (data) => {
    const { status, type, index, action } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRun(false);
      localStorage.setItem(seenKey(user.id), "1");
      return;
    }
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      setStepIndex(action === ACTIONS.PREV ? Math.max(index - 1, 0) : index + 1);
    }
  };

  if (!user || !run) return null;
  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      onEvent={onEvent}
      options={DEFAULT_OPTIONS}
      styles={STYLES}
      locale={{
        back: t("beta_tour.back"), close: t("beta_tour.last"), last: t("beta_tour.last"),
        next: t("beta_tour.next"), nextWithProgress: t("beta_tour.progress"), skip: t("beta_tour.skip"),
      }}
    />
  );
}
